import { $body } from './utils/wp.js'
import { Status } from './utils/constants.js'
import Navbar from './components/navbar.js'
import { createSlide } from './slide.js'

const $ = jQuery

export class Journey {
    constructor(...gsapPlugins) {
        gsap.registerPlugin(...gsapPlugins)

        // Initialize stages
        this._stages = Array.from(document.querySelectorAll('.stage'))

        // Initialize indicators
        this._indicators = [null].concat(Array.from(document.querySelectorAll('.stage-indicator')))

        // Initialize stage selectors
        this._stageSelectorInitial = document.querySelector('.stage-selector--initial')
        this._stageSelectorInline = document.querySelector('.stage-selector--inline')
        this._$stageSelectorInitial = $(this._stageSelectorInitial)
        this._$stageSelectorInline = $(this._stageSelectorInline)
        this._$stageSelectors = this.$stageSelectorInitial.add(this.$stageSelectorInline)

        // Keep track of initialized stages
        this._initializedStages = Array(this._stages.length).fill(false)

        // Keep track of timelines that have a side-step (They must be reset with seek(0) when reentering the stage)
        this._sideStepTimelines = []
    }

    set stage(stageID) {
        this._currentStage = this._stages[stageID]
        if (stageID > 0) {
            this.indicator = stageID
        }
    }

    get stage() {
        return this._currentStage
    }

    get stageID() {
        return this._currentStage.id.split('-').pop()
    }

    get stageSelectorInitial() {
        return this._stageSelectorInitial
    }

    get stageSelectorInline() {
        return this._stageSelectorInline
    }

    get $stageSelectorInitial() {
        return this._$stageSelectorInitial
    }

    get $stageSelectorInline() {
        return this._$stageSelectorInline
    }

    get $stageSelectors() {
        return this._$stageSelectors
    }

    getStage(stageID) {
        return this._stages[stageID]
    }

    getStages() {
        return this._stages
    }

    getNextStageID(ignoreCompleted = false) {
        let stageID = +this.stageID + 1
        if (!ignoreCompleted) {
            return stageID < this._stages.length ? stageID : null
        }

        for (; stageID < this._stages.length; stageID++) {
            const $stageSelectorItem = this.$stageSelectorInline
                .find('.stage-selector-item')
                .filter((i, el) => el.querySelector(`a[href="#stage-${stageID}"]`))

            if ($stageSelectorItem.data('status') !== Status.COMPLETED.description) {
                return stageID
            }
        }
        for (stageID = 0; stageID < this.stageID; stageID++) {
            const $stageSelectorItem = this.$stageSelectorInline
                .find('.stage-selector-item')
                .filter((i, el) => el.querySelector(`a[href="#stage-${stageID}"]`))

            if ($stageSelectorItem.data('status') !== Status.COMPLETED.description) {
                return stageID
            }
        }
        return null
    }

    set step(stepID) {
        this._currentStep = this._currentStage.querySelector(`#step-${stepID}`)
    }

    get step() {
        return this._currentStep
    }

    getStep(stepID, stageID) {
        const stage = stageID ? this._stages[stageID] : this._currentStage
        return stage?.querySelector(`#step-${stepID}`)
    }

    getNextStep() {
        let stepID = this.step.id.split('-').slice(1)
        stepID[stepID.length - 1]++
        return this.getStep(stepID.join('-'))
    }

    getSlides(stageID) {
        const stage = stageID !== undefined ? this._stages[stageID] : this._currentStage
        return Array.from(stage.querySelectorAll('.slide'))
    }

    initializeSlides(stageID) {
        if (this.isInitialized(stageID)) {
            return false
        }
        this.stage = stageID
        const slides = this.getSlides(stageID)
        slides.forEach((slideEl) => {
            this.step = slideEl.id
            const slide = createSlide(slideEl.id)
            slide.init()
        })
        this.setInitialized(stageID)
    }

    set indicator(indicatorID) {
        this._currentIndicator = this._indicators[indicatorID]
    }

    get indicator() {
        return this._currentIndicator
    }

    getIndicator(indicatorID) {
        return this._indicators[indicatorID]
    }

    getIndicators() {
        return this._indicators
    }

    get sideStepTimelines() {
        return this._sideStepTimelines
    }

    addSideStepTimeline(timeline) {
        this._sideStepTimelines.push(timeline)
    }

    isInitialized(stageID) {
        return this._initializedStages[stageID]
    }

    setInitialized(stageID) {
        this._initializedStages[stageID] = true
    }

    pinWhileAnimating(args) {
        return ScrollTrigger.create({
            trigger: this._currentStep,
            start: 'top top',
            end: '+=100%',
            pin: true,
            // anticipatePin: 1, // When scrolling fast, this causes onLeaveBackStage to trigger on enter stage
            ...args,
        })
    }

    pinWhileAnimatingResponsive(args) {
        const journey = this

        return ScrollTrigger.matchMedia({
            '(max-width: 575.98px)': function () {
                journey.pinWhileAnimating({ pinSpacing: false })
            },
            '(min-width: 576px)': function () {
                journey.pinWhileAnimating()
            },
            ...args,
        })
    }

    pinWhileTransitioning(args) {
        return ScrollTrigger.create({
            trigger: this._currentStep,
            start: 'top top-=1px', // Offset by -1px so it gets triggered just after pinWhileAnimating ends
            end: '+=100%',
            pin: true,
            pinSpacing: false,
            ...args,
        })
    }

    triggerAnimation(args) {
        return ScrollTrigger.create({
            trigger: this._currentStep,
            start: 'top top',
            end: '+=100%',
            scrub: 2,
            ...args,
        })
    }

    showStageSelector(args, pin = true) {
        const stageIdNumber = this.stageID
        const stageIdAttr = this.stage.id
        const nextStageID = this.getNextStageID(true)
        const navbar = Navbar.getInstance()

        // Reset the previous next stage before setting the new one
        this.$stageSelectors
            .find(`.stage-selector-item[data-status="${Status.NEXT.description}"]`)
            .attr('data-status', null)

        if (nextStageID !== null) {
            this.$stageSelectors
                .find('.stage-selector-item')
                .filter((i, el) => el.querySelector(`a[href="#stage-${nextStageID}"]`))
                .attr('data-status', Status.NEXT.description)
        }

        const stageSelectorST = ScrollTrigger.create({
            id: `stage-selector-${stageIdNumber}`,
            trigger: this.$stageSelectorInline,
            start: `top top+=${Navbar.getInstance()?.height}px`,
            anticipatePin: 1,
            invalidateOnRefresh: true,
            onToggle: (self) => {
                if (!self.isActive) {
                    return
                }
                if (pin) {
                    navbar.navAnimation?.reverse()
                    $body.removeClass('navbar-hidden')
                    $body.addClass('selector-visible selector-visible--inline')
                }

                const $selectedStageAll = this.$stageSelectors
                    .find('.stage-selector-item')
                    .filter((i, el) => el.querySelector(`a[href="#${stageIdAttr}"]`))

                $selectedStageAll.attr('data-status', Status.COMPLETED.description)
                this.$stageSelectorInline.removeClass('disabled')

                // Disable so it only gets triggered once
                self.disable()
            },
            ...args,
        })

        stageSelectorST.disable() // Disabled by default so it calculates start position correctly when the journey starts.

        return stageSelectorST
    }

    leaveSideStep(stageID) {}

    enterSideStep(stageID) {}

    enterStage(stageID) {
        if (stageID > 0) {
            this.getIndicators().forEach((indicator) => {
                indicator?.classList.remove('current')
            })
            this.getIndicator(stageID).classList.add('current')
        }
        this.getStages().forEach((stage) => {
            stage?.classList.remove('current-stage')
        })
        this.getStage(stageID).classList.add('current-stage')
    }

    leaveBackStage(stageID) {
        this.getStage(stageID).classList.remove('current-stage')

        if (stageID > 0) {
            this.getIndicator(stageID).classList.remove('current')
        }

        if (stageID > 1) {
            const prevStageID = stageID - 1
            this.getStage(prevStageID).classList.add('current-stage')
            this.getIndicator(prevStageID).classList.add('current')
        }
    }

    leaveStage(stageID, scrollTrigger) {}

    enterBackStage(stageID, scrollTrigger) {
        if ($body.hasClass('selector-visible')) {
            return
        }
    }

    handleScrollToSide(step, tl_step) {
        const scrollBtn = document.querySelector(`[data-scroll="side"][href="#${step.id}"]`)
        const offsetBottom = window.innerHeight / 1.5

        scrollBtn.addEventListener('click', (e) => {
            e.preventDefault()
            gsap.to(window, {
                duration: 2,
                scrollTo: tl_step.scrollTrigger.labelToScroll('side-step-shown') + offsetBottom,
            })
        })
    }
}
